import React from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
import { buildGaussianSeries, getAllCodes } from "../Statistics/utils";
import InfoHelpModal from "./InfoHelpModal";

const MARKER_STYLES = {
  yellow: { text: "text-yellow-600", dot: "bg-yellow-600" },
  blue: { text: "text-blue-600", dot: "bg-blue-600" },
  green: { text: "text-green-600", dot: "bg-green-600" },
};

const buildSteppedNumericTicks = (min, max, step) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(step) || step <= 0) {
    return [];
  }
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);

  const values = [lo];
  const start = Math.ceil(lo / step) * step;
  for (let value = start; value <= hi; value += step) {
    values.push(Number(value.toFixed(6)));
  }
  values.push(hi);

  return values
    .sort((a, b) => a - b)
    .filter((value, index, array) => index === 0 || Math.abs(value - array[index - 1]) > 1e-6);
};

const formatNumericTickLabel = (value) => {
  if (!Number.isFinite(value)) return "";
  if (Math.abs(value - Math.round(value)) < 1e-6) return String(Math.round(value));
  return Number(value).toFixed(1).replace(/\.0$/, "");
};

const getTickLabelWidthPct = (tickCount) => {
  if (tickCount <= 1) return 60;
  const slot = 100 / (tickCount - 1);
  return Math.max(14, Math.min(28, slot * 0.86));
};

const getTickLabelAnchorStyle = ({ index, total, pct, widthPct }) => {
  if (total <= 1) {
    return {
      left: "50%",
      transform: "translateX(-50%)",
      textAlign: "center",
      width: `${widthPct}%`,
    };
  }

  if (index === 0) {
    return {
      left: "0%",
      transform: "translateX(0)",
      textAlign: "left",
      width: `${widthPct}%`,
    };
  }

  if (index === total - 1) {
    return {
      left: "100%",
      transform: "translateX(-100%)",
      textAlign: "right",
      width: `${widthPct}%`,
    };
  }

  return {
    left: `${pct}%`,
    transform: "translateX(-50%)",
    textAlign: "center",
    width: `${widthPct}%`,
  };
};

const formatCountValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Math.abs(numeric - Math.round(numeric)) < 1e-6) return Math.round(numeric).toLocaleString();
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
};

const formatPercentValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0.0%";
  return `${numeric.toFixed(1)}%`;
};

const sortStatsByMode = (stats, sortBy) => {
  switch (sortBy) {
    case "label_desc":
      return [...stats].sort((a, b) => String(b.label).localeCompare(String(a.label)));
    case "size_desc":
      return [...stats].sort((a, b) => b.sampleSize - a.sampleSize);
    case "size_asc":
      return [...stats].sort((a, b) => a.sampleSize - b.sampleSize);
    case "label_asc":
    default:
      return [...stats].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }
};

const buildStackedMarkers = ({ markers, toPct, clamp, offsetStep }) => {
  const epsilon = 1.5;
  const clusters = {};

  markers.forEach((marker) => {
    const pct = toPct(clamp(marker.val));
    const key = Object.keys(clusters).find(
      (clusterKey) => Math.abs(Number(clusterKey) - pct) < epsilon
    );
    const bucket = key ?? pct;
    (clusters[bucket] ||= []).push({ ...marker, pct });
  });

  return Object.values(clusters).flatMap((cluster) => {
    cluster.sort((a, b) => b.pr - a.pr);
    return cluster.map((marker, index) => ({ ...marker, offset: index * offsetStep }));
  });
};

const MiniRangeBar = ({
  avg,
  med,
  sd,
  allCodes,
  depType,
  mappingMin,
  mappingMax,
  range,
  getAnswerLabel,
}) => {
  const clampLocal = (value) => Math.max(mappingMin, Math.min(mappingMax, value));
  const toPctLocal = (value) => ((value - mappingMin) / (range || 1)) * 100;

  let ticks = [];
  if (depType === "cat") {
    const labels = allCodes.map(getAnswerLabel);
    const n = Math.max(1, labels.length);
    ticks = labels.map((label, index) => ({
      pct: n === 1 ? 50 : (index / (n - 1)) * 100,
      label,
    }));
  } else if (depType === "num") {
    ticks = buildSteppedNumericTicks(mappingMin, mappingMax, 25).map((code) => ({
      pct: toPctLocal(code),
      label: formatNumericTickLabel(code),
    }));
  } else {
    ticks = allCodes.map((code) => ({
      pct: toPctLocal(code),
      label: String(getAnswerLabel(code)),
    }));
  }

  const markers = [
    { key: "-σ", val: avg - sd, lbl: "-StdDev", cls: "yellow", pr: 2 },
    { key: "+σ", val: avg + sd, lbl: "+StdDev", cls: "yellow", pr: 2 },
    { key: "avg", val: avg, lbl: "Avg", cls: "blue", pr: 0 },
    { key: "med", val: med, lbl: "Med", cls: "green", pr: 1 },
  ];
  const stacked = buildStackedMarkers({
    markers,
    toPct: toPctLocal,
    clamp: clampLocal,
    offsetStep: 18,
  });
  const tickLabelWidthPct = getTickLabelWidthPct(ticks.length);

  return (
    <div className="mt-3">
      <div className="relative w-full overflow-hidden" style={{ height: 74 }}>
        <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 bg-gray-200 rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-2 bg-yellow-300 opacity-50"
          style={{
            left: `${toPctLocal(clampLocal(avg - sd))}%`,
            width: `${toPctLocal(clampLocal(avg + sd)) - toPctLocal(clampLocal(avg - sd))}%`,
          }}
        />
        {stacked.map((marker) => {
          const markerStyle = MARKER_STYLES[marker.cls] || MARKER_STYLES.blue;
          return (
            <div
              key={`${marker.key}-${marker.offset}`}
              className="absolute text-center"
              style={{
                left: `${marker.pct}%`,
                top: -marker.offset,
                transform: "translateX(-50%)",
              }}
            >
              <div className={`text-[10px] font-semibold mb-0.5 ${markerStyle.text}`}>
                {marker.lbl}
              </div>
              <div className={`w-2.5 h-2.5 rounded-full mx-auto ${markerStyle.dot}`} />
            </div>
          );
        })}
        {ticks.map(({ pct, label }, index) => {
          const tickAnchor = getTickLabelAnchorStyle({
            index,
            total: ticks.length,
            pct,
            widthPct: tickLabelWidthPct,
          });

          return (
            <React.Fragment key={`${pct}-${index}`}>
              <div
                className="absolute"
                style={{ left: `${pct}%`, bottom: 22, transform: "translateX(-50%)" }}
              >
                <div className="w-1.5 h-1.5 bg-gray-600 rounded-full mx-auto" />
              </div>
              <div
                className="absolute text-[10px] text-gray-600 leading-[1.15] whitespace-normal break-words"
                style={{ ...tickAnchor, bottom: 0 }}
                title={String(label)}
              >
                {label}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const CategoryStatsCard = ({
  group,
  fallbackLabel,
  yearLabel,
  depType,
  sampleSizeLabel,
  allCodes,
  domainMin,
  domainMax,
  getAnswerLabel,
  showCategoryResponseFrequencies,
}) => {
  const label = group?.label || fallbackLabel || "Category";
  const safeDomainMin = Number.isFinite(domainMin) ? domainMin : 0;
  const safeDomainMax = Number.isFinite(domainMax) ? domainMax : 1;
  const range = safeDomainMax - safeDomainMin || 1;

  return (
    <div className="bg-gray-100 p-4 rounded-lg shadow-sm h-full">
      <h3 className="text-xl font-bold text-gray-800 mb-1 leading-tight break-words">{label}</h3>
      {yearLabel && <p className="text-xs text-gray-500 mb-2">{yearLabel}</p>}

      {!group ? (
        <p className="text-sm text-gray-600">
          No data for this category in {yearLabel || "this year"}.
        </p>
      ) : depType === "cat" ? (
        <>
          <p>
            <strong>{sampleSizeLabel}:</strong> {formatCountValue(group.sampleSize)}
          </p>
          <MiniRangeBar
            avg={group.average}
            med={group.median}
            sd={group.stdDev}
            allCodes={allCodes}
            depType={depType}
            mappingMin={safeDomainMin}
            mappingMax={safeDomainMax}
            range={range}
            getAnswerLabel={getAnswerLabel}
          />
          {showCategoryResponseFrequencies &&
            Array.isArray(group.responseFrequencies) &&
            group.responseFrequencies.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-xs font-semibold text-gray-700">Response Frequencies</p>
                {group.responseFrequencies.map((entry) => (
                  <div
                    key={`${entry.code}-${entry.label}`}
                    className="flex items-start justify-between gap-2 text-xs text-gray-700"
                  >
                    <span className="flex-1">{entry.label}</span>
                    <span className="whitespace-nowrap font-medium">
                      {formatPercentValue(entry.pct)}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </>
      ) : (
        <>
          <p>
            <strong>Average:</strong> {group.average.toFixed(1)}
          </p>
          <p>
            <strong>Median:</strong> {group.median.toFixed(1)}
          </p>
          <p>
            <strong>Std. Deviation:</strong> {group.stdDev}
          </p>
          <p>
            <strong>{sampleSizeLabel}:</strong> {formatCountValue(group.sampleSize)}
          </p>
          <p>
            <strong>Range:</strong> {group.min} – {group.max}
          </p>
          <MiniRangeBar
            avg={group.average}
            med={group.median}
            sd={group.stdDev}
            allCodes={allCodes}
            depType={depType}
            mappingMin={safeDomainMin}
            mappingMax={safeDomainMax}
            range={range}
            getAnswerLabel={getAnswerLabel}
          />
        </>
      )}
    </div>
  );
};

const ChartStatisticsPanel = ({
  nationalStats,
  selectedDepMeta,
  dependentAnswerMapping,
  sortBy,
  setSortBy,
  indepStats,
  selectedIndepBucket,
  setSelectedIndepBucket,
  indepIsGrouped,
  detectNumericScale,
  useWeightedData = false,
  hideByCategory = false,
  showCategoryResponseFrequencies = false,
  categoryLabel = "Category",
}) => {
  if (!nationalStats) return null;

  const depType = selectedDepMeta?.type ?? "cat";
  const allCodes = getAllCodes(dependentAnswerMapping);
  const { average, median, stdDev } = nationalStats;
  const mappingMin = allCodes[0] ?? 0;
  const mappingMax = allCodes[allCodes.length - 1] ?? 1;
  const domainMin = depType === "num" ? nationalStats.min : mappingMin;
  const domainMax = depType === "num" ? nationalStats.max : mappingMax;
  const range = domainMax - domainMin || 1;
  const scale = detectNumericScale(allCodes);
  const clamp = (value) => Math.max(domainMin, Math.min(domainMax, value));
  const toPct = (value) => ((value - domainMin) / range) * 100;
  const gaussianSeries = buildGaussianSeries(average, stdDev, domainMin, domainMax);
  const getAnswerLabel = (code) => dependentAnswerMapping[String(code)]?.Display ?? `Value ${code}`;

  const visibleIndepStats = (() => {
    const sorted = sortStatsByMode(indepStats, sortBy);
    if (selectedIndepBucket === "All") return sorted;
    return sorted.filter((stat) => stat.label === selectedIndepBucket);
  })();
  const hasComparisonStats = indepStats.length > 0;
  const sampleSizeLabel = useWeightedData ? "Weighted Sample Size" : "Sample Size";

  let rangeTicks = [];
  if (depType === "cat") {
    const labels = allCodes.map(getAnswerLabel);
    const n = Math.max(1, labels.length);
    rangeTicks = labels.map((label, index) => ({
      pct: n === 1 ? 50 : (index / (n - 1)) * 100,
      label,
    }));
  } else if (depType === "num") {
    rangeTicks = buildSteppedNumericTicks(domainMin, domainMax, 10).map((code) => ({
      pct: toPct(code),
      label: formatNumericTickLabel(code),
    }));
  } else {
    if (scale === "0-100") {
      rangeTicks = buildSteppedNumericTicks(mappingMin, mappingMax, 10).map((code) => ({
        pct: ((code - mappingMin) / (mappingMax - mappingMin || 1)) * 100,
        label: formatNumericTickLabel(code),
      }));
    } else {
      rangeTicks = allCodes.map((code) => ({
        pct: ((code - mappingMin) / (mappingMax - mappingMin || 1)) * 100,
        label: String(getAnswerLabel(code)),
      }));
    }
  }

  const markers = [
    { key: "-σ", val: average - stdDev, lbl: "-StdDev", cls: "yellow", pr: 2 },
    { key: "+σ", val: average + stdDev, lbl: "+StdDev", cls: "yellow", pr: 2 },
    { key: "avg", val: average, lbl: "Avg", cls: "blue", pr: 0 },
    { key: "med", val: median, lbl: "Med", cls: "green", pr: 1 },
  ];
  const stackedMarkers = buildStackedMarkers({
    markers,
    toPct,
    clamp,
    offsetStep: 24,
  });
  const rangeTickLabelWidthPct = getTickLabelWidthPct(rangeTicks.length);

  return (
    <div className="w-full mt-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-800">National Overview</h2>
        <InfoHelpModal
          ariaLabel="Show definitions for mean, median, and standard deviation"
          title="Statistics"
        >
          <div>
            <p className="font-semibold text-[#181010]">Median</p>
            <p>
              The median is the middle response value after all responses are ordered from low to
              high. Half of responses fall below it and half fall above it.
            </p>
          </div>
          <div>
            <p className="font-semibold text-[#181010]">Mean</p>
            <p>The mean is the average of all response values.</p>
          </div>
          <div>
            <p className="font-semibold text-[#181010]">Standard Deviation</p>
            <p>
              Standard deviation shows how spread out responses are around the mean. A low standard
              deviation means responses are clustered near the mean, while a high standard deviation
              means responses vary more widely and may include more extreme values.
            </p>
          </div>
        </InfoHelpModal>
      </div>

      {depType === "cat" ? (
        <div className="grid grid-cols-1 gap-4 mb-6">
          <div className="bg-gray-100 p-4 rounded-lg shadow-sm text-center">
            <p className="text-sm text-gray-500">{sampleSizeLabel}</p>
            <p className="text-2xl font-bold text-gray-800">
              {formatCountValue(nationalStats.sampleSize)}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-100 p-4 rounded-lg shadow-sm text-center">
            <p className="text-sm text-gray-500">{sampleSizeLabel}</p>
            <p className="text-2xl font-bold text-gray-800">
              {formatCountValue(nationalStats.sampleSize)}
            </p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg shadow-sm text-center">
            <p className="text-sm text-gray-500">National Average</p>
            <p className="text-2xl font-bold text-gray-800">{nationalStats.average.toFixed(2)}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg shadow-sm text-center">
            <p className="text-sm text-gray-500">National Median</p>
            <p className="text-2xl font-bold text-gray-800">{nationalStats.median.toFixed(2)}</p>
          </div>
          <div className="bg-gray-100 p-4 rounded-lg shadow-sm text-center">
            <p className="text-sm text-gray-500">National Std Dev</p>
            <p className="text-2xl font-bold text-gray-800">{nationalStats.stdDev}</p>
          </div>
        </div>
      )}

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 text-center">Range</h3>

        <div className="relative w-full overflow-hidden" style={{ height: 92 }}>
          <div className="absolute top-1/2 -translate-y-1/2 w-full h-2 bg-gray-200 rounded-full" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-2 bg-yellow-300 opacity-50"
            style={{
              left: `${toPct(clamp(average - stdDev))}%`,
              width: `${toPct(clamp(average + stdDev)) - toPct(clamp(average - stdDev))}%`,
            }}
          />

          {stackedMarkers.map((marker) => {
            const markerStyle = MARKER_STYLES[marker.cls] || MARKER_STYLES.blue;
            return (
              <div
                key={`${marker.key}-${marker.offset}`}
                className="absolute text-center"
                style={{
                  left: `${marker.pct}%`,
                  top: -marker.offset,
                  transform: "translateX(-50%)",
                }}
              >
                <div className={`text-xs font-semibold mb-1 ${markerStyle.text}`}>{marker.lbl}</div>
                <div className={`w-3 h-3 rounded-full mx-auto ${markerStyle.dot}`} />
              </div>
            );
          })}

          {rangeTicks.map(({ pct, label }, index) => {
            const tickAnchor = getTickLabelAnchorStyle({
              index,
              total: rangeTicks.length,
              pct,
              widthPct: rangeTickLabelWidthPct,
            });

            return (
              <React.Fragment key={`${pct}-${index}`}>
                <div
                  className="absolute"
                  style={{ left: `${pct}%`, bottom: 28, transform: "translateX(-50%)" }}
                >
                  <div className="w-2 h-2 bg-gray-600 rounded-full mx-auto" />
                </div>
                <div
                  className="absolute text-xs text-gray-600 leading-[1.2] whitespace-normal break-words"
                  style={{ ...tickAnchor, bottom: 0 }}
                  title={String(label)}
                >
                  {label}
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {depType === "num" && gaussianSeries.length > 0 && (
          <div className="w-full mt-2">
            <svg width="100%" height="140">
              {(() => {
                const w = 800;
                const h = 120;
                const padL = 40;
                const padR = 20;
                const padT = 10;
                const padB = 20;
                const innerW = w - padL - padR;
                const innerH = h - padT - padB;
                const xs = d3.scaleLinear().domain([domainMin, domainMax]).range([0, innerW]);
                const ys = d3.scaleLinear().domain([0, 1]).range([innerH, 0]);
                const area = d3
                  .area()
                  .x((d) => xs(d.x))
                  .y0(ys(0))
                  .y1((d) => ys(d.y))
                  .curve(d3.curveMonotoneX);
                const line = d3
                  .line()
                  .x((d) => xs(d.x))
                  .y((d) => ys(d.y))
                  .curve(d3.curveMonotoneX);
                const areaPath = area(gaussianSeries);
                const linePath = line(gaussianSeries);
                return (
                  <g transform={`translate(${padL},${padT})`}>
                    <path d={areaPath} fill="#4f46e5" opacity="0.25" />
                    <path d={linePath} fill="none" stroke="#4f46e5" strokeWidth="2" />
                  </g>
                );
              })()}
            </svg>
          </div>
        )}
      </div>

      {!hideByCategory && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {indepIsGrouped ? "By Group" : `By ${categoryLabel}`}
          </h2>

          {!hasComparisonStats ? (
            <p className="text-sm text-gray-600">No comparison category selected.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {indepIsGrouped ? "Select Group" : `Select ${categoryLabel}`}
                  </label>
                  <select
                    value={selectedIndepBucket}
                    onChange={(event) => setSelectedIndepBucket(event.target.value)}
                    className="w-full h-12 rounded-lg border border-gray-300 p-2"
                  >
                    <option value="All">All</option>
                    {indepStats.map((stat) => (
                      <option key={stat.label} value={stat.label}>
                        {stat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Sort</label>
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                    className="w-full h-12 rounded-lg border border-gray-300 p-2"
                  >
                    <option value="label_asc">{`${categoryLabel} (A → Z)`}</option>
                    <option value="label_desc">{`${categoryLabel} (Z → A)`}</option>
                    <option value="size_desc">Sample size (High → Low)</option>
                    <option value="size_asc">Sample size (Low → High)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {visibleIndepStats.map((group, index) => (
                  <CategoryStatsCard
                    key={`${group.label}-${index}`}
                    group={group}
                    depType={depType}
                    sampleSizeLabel={sampleSizeLabel}
                    allCodes={allCodes}
                    domainMin={domainMin}
                    domainMax={domainMax}
                    getAnswerLabel={getAnswerLabel}
                    showCategoryResponseFrequencies={showCategoryResponseFrequencies}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const buildStatsDomain = ({ depType, nationalStats, allCodes }) => {
  const mappingMin = allCodes[0] ?? 0;
  const mappingMax = allCodes[allCodes.length - 1] ?? 1;
  const domainMin = depType === "num" ? (nationalStats?.min ?? mappingMin) : mappingMin;
  const domainMax = depType === "num" ? (nationalStats?.max ?? mappingMax) : mappingMax;

  if (domainMin === domainMax) {
    return { domainMin, domainMax: domainMax + 1 };
  }

  return { domainMin, domainMax };
};

export const ChartStatisticsComparisonByCategoryPanel = ({
  baseYearLabel,
  comparisonYearLabel,
  selectedDepMeta,
  baseNationalStats,
  comparisonNationalStats,
  baseDependentAnswerMapping,
  comparisonDependentAnswerMapping,
  baseIndepStats,
  comparisonIndepStats,
  selectedIndepBucket,
  setSelectedIndepBucket,
  sortBy,
  setSortBy,
  indepIsGrouped,
  useWeightedData = false,
}) => {
  const depType = selectedDepMeta?.type ?? "cat";
  const sampleSizeLabel = useWeightedData ? "Weighted Sample Size" : "Sample Size";

  const baseAllCodes = getAllCodes(baseDependentAnswerMapping);
  const comparisonAllCodes = getAllCodes(comparisonDependentAnswerMapping);
  const baseDomain = buildStatsDomain({
    depType,
    nationalStats: baseNationalStats,
    allCodes: baseAllCodes,
  });
  const comparisonDomain = buildStatsDomain({
    depType,
    nationalStats: comparisonNationalStats,
    allCodes: comparisonAllCodes,
  });

  const getBaseAnswerLabel = (code) =>
    baseDependentAnswerMapping[String(code)]?.Display ?? `Value ${code}`;
  const getComparisonAnswerLabel = (code) =>
    comparisonDependentAnswerMapping[String(code)]?.Display ?? `Value ${code}`;

  const baseByLabel = new Map((baseIndepStats || []).map((stat) => [String(stat.label), stat]));
  const comparisonByLabel = new Map(
    (comparisonIndepStats || []).map((stat) => [String(stat.label), stat])
  );
  const allLabels = Array.from(new Set([...baseByLabel.keys(), ...comparisonByLabel.keys()]));

  const sortedLabels = [...allLabels].sort((left, right) => {
    if (sortBy === "label_desc") return String(right).localeCompare(String(left));
    if (sortBy === "size_desc") {
      const leftSize =
        baseByLabel.get(left)?.sampleSize ?? comparisonByLabel.get(left)?.sampleSize ?? 0;
      const rightSize =
        baseByLabel.get(right)?.sampleSize ?? comparisonByLabel.get(right)?.sampleSize ?? 0;
      return rightSize - leftSize;
    }
    if (sortBy === "size_asc") {
      const leftSize =
        baseByLabel.get(left)?.sampleSize ?? comparisonByLabel.get(left)?.sampleSize ?? 0;
      const rightSize =
        baseByLabel.get(right)?.sampleSize ?? comparisonByLabel.get(right)?.sampleSize ?? 0;
      return leftSize - rightSize;
    }
    return String(left).localeCompare(String(right));
  });

  const effectiveSelectedIndepBucket =
    selectedIndepBucket === "All" || sortedLabels.includes(selectedIndepBucket)
      ? selectedIndepBucket
      : "All";

  const visibleLabels =
    effectiveSelectedIndepBucket === "All"
      ? sortedLabels
      : sortedLabels.filter((label) => label === effectiveSelectedIndepBucket);

  const hasComparisonStats = sortedLabels.length > 0;

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">By Category</h2>

      {!hasComparisonStats ? (
        <p className="text-sm text-gray-600">No comparison category selected.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                {indepIsGrouped ? "Select Group" : "Select Category"}
              </label>
              <select
                value={effectiveSelectedIndepBucket}
                onChange={(event) => setSelectedIndepBucket(event.target.value)}
                className="w-full h-12 rounded-lg border border-gray-300 p-2"
              >
                <option value="All">All</option>
                {sortedLabels.map((label) => (
                  <option key={label} value={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Sort</label>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="w-full h-12 rounded-lg border border-gray-300 p-2"
              >
                <option value="label_asc">Category (A → Z)</option>
                <option value="label_desc">Category (Z → A)</option>
                <option value="size_desc">Sample size (High → Low)</option>
                <option value="size_asc">Sample size (Low → High)</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {visibleLabels.map((label) => (
              <div key={label} className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <CategoryStatsCard
                  group={baseByLabel.get(label) || null}
                  fallbackLabel={label}
                  yearLabel={baseYearLabel}
                  depType={depType}
                  sampleSizeLabel={sampleSizeLabel}
                  allCodes={baseAllCodes}
                  domainMin={baseDomain.domainMin}
                  domainMax={baseDomain.domainMax}
                  getAnswerLabel={getBaseAnswerLabel}
                />
                <CategoryStatsCard
                  group={comparisonByLabel.get(label) || null}
                  fallbackLabel={label}
                  yearLabel={comparisonYearLabel}
                  depType={depType}
                  sampleSizeLabel={sampleSizeLabel}
                  allCodes={comparisonAllCodes}
                  domainMin={comparisonDomain.domainMin}
                  domainMax={comparisonDomain.domainMax}
                  getAnswerLabel={getComparisonAnswerLabel}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

MiniRangeBar.propTypes = {
  avg: PropTypes.number.isRequired,
  med: PropTypes.number.isRequired,
  sd: PropTypes.number.isRequired,
  allCodes: PropTypes.array.isRequired,
  depType: PropTypes.string.isRequired,
  mappingMin: PropTypes.number.isRequired,
  mappingMax: PropTypes.number.isRequired,
  range: PropTypes.number.isRequired,
  getAnswerLabel: PropTypes.func.isRequired,
  showCategoryResponseFrequencies: PropTypes.bool,
};

CategoryStatsCard.propTypes = {
  group: PropTypes.shape({
    label: PropTypes.string,
    average: PropTypes.number,
    median: PropTypes.number,
    stdDev: PropTypes.number,
    sampleSize: PropTypes.number,
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  fallbackLabel: PropTypes.string,
  yearLabel: PropTypes.string,
  depType: PropTypes.string.isRequired,
  sampleSizeLabel: PropTypes.string.isRequired,
  allCodes: PropTypes.array.isRequired,
  domainMin: PropTypes.number,
  domainMax: PropTypes.number,
  getAnswerLabel: PropTypes.func.isRequired,
};

ChartStatisticsPanel.propTypes = {
  nationalStats: PropTypes.shape({
    average: PropTypes.number,
    median: PropTypes.number,
    stdDev: PropTypes.number,
    sampleSize: PropTypes.number,
  }),
  selectedDepMeta: PropTypes.shape({ type: PropTypes.string }),
  dependentAnswerMapping: PropTypes.object.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  indepStats: PropTypes.array.isRequired,
  selectedIndepBucket: PropTypes.string.isRequired,
  setSelectedIndepBucket: PropTypes.func.isRequired,
  indepIsGrouped: PropTypes.bool.isRequired,
  detectNumericScale: PropTypes.func.isRequired,
  useWeightedData: PropTypes.bool,
  hideByCategory: PropTypes.bool,
  showCategoryResponseFrequencies: PropTypes.bool,
  categoryLabel: PropTypes.string,
};

ChartStatisticsComparisonByCategoryPanel.propTypes = {
  baseYearLabel: PropTypes.string.isRequired,
  comparisonYearLabel: PropTypes.string.isRequired,
  selectedDepMeta: PropTypes.shape({ type: PropTypes.string }),
  baseNationalStats: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  comparisonNationalStats: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
  }),
  baseDependentAnswerMapping: PropTypes.object.isRequired,
  comparisonDependentAnswerMapping: PropTypes.object.isRequired,
  baseIndepStats: PropTypes.array.isRequired,
  comparisonIndepStats: PropTypes.array.isRequired,
  selectedIndepBucket: PropTypes.string.isRequired,
  setSelectedIndepBucket: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  setSortBy: PropTypes.func.isRequired,
  indepIsGrouped: PropTypes.bool.isRequired,
  useWeightedData: PropTypes.bool,
};

export default React.memo(ChartStatisticsPanel);
